import React from 'react';
import PineconeConnectionBox from '../Connections/PineconeConnectionBox';
import AWSConnectionBox from '../Connections/AWSConnectionBox';
import MongoDBConnectionBox from '../Connections/MongoDBConnectionBox';
import PineconeConnection from '../../../models/Connections/PineconeConnection';
import AWSConnection from '../../../models/Connections/AWSConnection';
import MongoDBConnection from '../../../models/Connections/MongoDBConnection';
import SemanticMemory from '../../../models/Memories/SemanticMemory'; 
import { 
  TextField, 
  Button, 
  Card, 
  CardContent,
  Grid, 
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  Box,
  Paper,
  IconButton,
  Stack
} from '@mui/material';
import { Database, Search, Cloud } from 'lucide-react';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

interface SemanticMemoryBoxProps {
  memory: SemanticMemory;
  onMemoryChange: (memory: SemanticMemory) => void;
}

const SemanticMemoryBox: React.FC<SemanticMemoryBoxProps> = ({ 
  memory,
  onMemoryChange
}) => { 
  const handleThresholdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value >= 0 && value <= 1) {
      onMemoryChange(new SemanticMemory(value, memory.connections));
    }
  };

  const handleStepChange = (step: number) => {
    const newValue = Math.min(Math.max(memory.similarityThreshold + step, 0), 1);
    onMemoryChange(new SemanticMemory(newValue, memory.connections));
  };

  const addConnection = (type: 'pinecone' | 'aws' | 'mongodb_json') => {
    let newConnection: PineconeConnection | AWSConnection | MongoDBConnection;
    
    if (type === 'pinecone') {
      newConnection = new PineconeConnection({
        indexName: '',
        namespace: '',
        k: 5,
        filter: {},
      });
    } else if (type === 'aws') {
      newConnection = new AWSConnection({
        awsAccessKeyId: '',
        awsSecretAccessKey: '',
        apigwConnectionId: ''
      });
    } else {
      newConnection = new MongoDBConnection({
        collection_name: '',
        connection_name: '',
        query: '',
        filter: {}
      });
    }
    
    onMemoryChange(new SemanticMemory(memory.similarityThreshold, [...memory.connections, newConnection]));
  };

  const handleConnectionUpdate = (updatedData: PineconeConnection | AWSConnection | MongoDBConnection) => {
    if (typeof updatedData.toJson !== 'function') {
      throw new Error('Attempted to update with a non-Connection instance');
    }
    const updatedConnections = memory.connections.map((conn) => 
      conn.id === updatedData.id ? updatedData : conn
    );
    onMemoryChange(new SemanticMemory(memory.similarityThreshold, updatedConnections));
  };

  const removeConnection = (id: string) => {
    const updatedConnections = memory.connections.filter((conn) => conn.id !== id);
    onMemoryChange(new SemanticMemory(memory.similarityThreshold, updatedConnections));
  };

  const connectionTypes = [
    {
      type: 'pinecone' as const,
      name: 'Pinecone',
      icon: <Search size={24} />,
      description: 'Vector database for similarity search',
      color: '#2196f3',
      bgcolor: '#e3f2fd'
    },
    {
      type: 'aws' as const,
      name: 'AWS',
      icon: <Cloud size={24} />,
      description: 'Amazon Web Services integration',
      color: '#ff9800',
      bgcolor: '#fff3e0'
    },
    {
      type: 'mongodb_json' as const,
      name: 'MongoDB',
      icon: <Database size={24} />,
      description: 'MongoDB vector search with JSON',
      color: '#4caf50',
      bgcolor: '#e8f5e8'
    }
  ];

  return (
    <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, border: 1, borderColor: 'grey.300' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Similarity Configuration</Typography>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Similarity Threshold
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleStepChange(-0.01)}
              >
                -
              </Button>
              <TextField
                value={memory.similarityThreshold.toFixed(2)}
                onChange={handleThresholdChange}
                size="small"
                sx={{ width: 100 }}
                inputProps={{ style: { textAlign: 'center' } }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleStepChange(0.01)}
              >
                +
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Minimum similarity score for relevant results (0.0 - 1.0)
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Vector Connections</Typography>
              <Typography variant="body2" color="text.secondary">
                {memory.connections.length} connection{memory.connections.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle1" gutterBottom>Add New Connection</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                  {connectionTypes.map((connectionType) => (
                    <Paper
                      key={connectionType.type}
                        sx={{
                          p: 2,
                        border: 1,
                        borderColor: 'divider',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                          '&:hover': {
                          borderColor: connectionType.color,
                          bgcolor: connectionType.bgcolor,
                          transform: 'translateY(-2px)',
                          boxShadow: 2
                        }
                        }}
                      onClick={() => addConnection(connectionType.type)}
                      >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box sx={{ color: connectionType.color }}>
                          {connectionType.icon}
                        </Box>
                        <Typography variant="subtitle2" fontWeight="medium">
                          {connectionType.name}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {connectionType.description}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              </Box>

              {memory.connections.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>Active Connections</Typography>
                  <Stack spacing={2}>
                    {memory.connections.map((connection, index) => (
                      <Paper key={connection.id} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight="medium">
                            {connection.type === 'pinecone' ? 'Pinecone' : 
                             connection.type === 'aws' ? 'AWS' : 'MongoDB'} Connection {index + 1}
                          </Typography>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeConnection(connection.id)}
                          >
                            <RemoveIcon />
                          </IconButton>
                        </Box>
                        
                        {connection.type === 'pinecone' && (
                          <PineconeConnectionBox
                            pinneconeConnectionData={connection as PineconeConnection}
                            onUpdate={handleConnectionUpdate}
                          />
                        )}
                        {connection.type === 'aws' && (
                          <AWSConnectionBox
                            awsConnectionData={connection as AWSConnection}
                            onUpdate={handleConnectionUpdate}
                          />
                        )}
                        {connection.type === 'mongodb_json' && (
                          <MongoDBConnectionBox
                            mongodbConnectionData={connection as MongoDBConnection}
                            onUpdate={handleConnectionUpdate}
                          />
                        )}
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default SemanticMemoryBox;